import { useEffect, useMemo, useState } from 'react'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Stack from 'react-bootstrap/Stack'

import {
  getGeographicSalesIntelligence,
  getOrgEventsForReport,
  getSeasons,
} from '../../utilities/api'
import { getDefaultSeason } from '../../utilities/helpers'
import { useReportRefresh } from '../../hooks/useReportRefresh'
import { EventSelector } from '../EventSelector'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { ReportRefreshButton } from '../ReportRefreshButton'
import GeographicSalesIntelligenceDistance from './GeographicSalesIntelligenceDistance'
import GeographicSalesIntelligenceHeatmap from './GeographicSalesIntelligenceHeatmap'
import GeographicSalesIntelligenceLocalMarket from './GeographicSalesIntelligenceLocalMarket'
import GeographicSalesIntelligenceStateMap from './GeographicSalesIntelligenceStateMap'
import GeographicSalesIntelligenceSummary from './GeographicSalesIntelligenceSummary'
import GeographicSalesIntelligenceTopMarkets from './GeographicSalesIntelligenceTopMarkets'

import './geographicSalesIntelligenceWrapper.scss'

export default function GeographicSalesIntelligenceWrapper({ organization }) {
  const [seasons, setSeasons] = useState([])
  const [events, setEvents] = useState([])
  const [seasonId, setSeasonId] = useState('')
  const [eventUUID, setEventUUID] = useState('')
  const [reportData, setReportData] = useState(null)
  const [isLoadingFilters, setIsLoadingFilters] = useState(true)
  const [isLoadingReport, setIsLoadingReport] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingReport)
  const organizationUUID = organization?.uuid

  useEffect(() => {
    if (!organizationUUID) return

    let cancelled = false
    setIsLoadingFilters(true)

    Promise.all([
      getSeasons(organizationUUID),
      getOrgEventsForReport(organizationUUID),
    ])
      .then(([seasonsRes, eventsRes]) => {
        if (cancelled) return
        const seasonList = Array.isArray(seasonsRes?.data)
          ? seasonsRes.data
          : []
        const eventList = Array.isArray(eventsRes?.data) ? eventsRes.data : []
        setSeasons(seasonList)
        setEvents(eventList)
        const current = getDefaultSeason(seasonList)
        setSeasonId(current?.id ? String(current.id) : '')
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load geographic sales filters', err)
        setLoadError('Could not load seasons and events for this organization.')
      })
      .finally(() => {
        if (!cancelled) setIsLoadingFilters(false)
      })

    return () => {
      cancelled = true
    }
  }, [organizationUUID])

  useEffect(() => {
    if (!organizationUUID || isLoadingFilters) return

    let cancelled = false
    setIsLoadingReport(true)
    setLoadError(null)

    getGeographicSalesIntelligence(organizationUUID, {
      seasonId,
      eventUUID,
    })
      .then((res) => {
        if (cancelled) return
        setReportData(res?.data ?? null)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load geographic sales intelligence', err)
        setLoadError('Could not load geographic sales intelligence.')
        setReportData(null)
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingReport(false)
          completeRefresh()
        }
      })

    return () => {
      cancelled = true
    }
  }, [organizationUUID, seasonId, eventUUID, refreshToken, isLoadingFilters])

  const selectedSeason = useMemo(
    () => seasons.find((season) => String(season.id) === String(seasonId)),
    [seasons, seasonId]
  )
  const selectedEvent = useMemo(
    () => events.find((event) => event.uuid === eventUUID) || null,
    [events, eventUUID]
  )

  const subtitle = [
    organization?.name,
    selectedEvent?.name,
    selectedSeason?.name,
  ]
    .filter(Boolean)
    .join(' · ')

  const isLoading = isLoadingFilters || isLoadingReport

  return (
    <section
      className="spacer-sm geographic-sales-intelligence-wrapper"
      id="geographic-sales-intelligence-wrapper"
    >
      <header className="section-header-sm report-page-header">
        <div className="section-heading">
          <h1>Geographic Sales Intelligence</h1>
        </div>
        <p className="section-header-desc">
          Distance, heatmap, local vs. out-of-market, and U.S. state sales for
          complete orders with a purchase location
          {organization?.name ? ` at ${organization.name}` : ''}.
        </p>
        {subtitle ? (
          <p className="section-header-desc mt-1">
            <strong>{subtitle}</strong>
          </p>
        ) : null}
      </header>

      <Card
        body
        className="card--xs card-with-border geographic-sales-intelligence-controls"
      >
        <Stack
          direction="horizontal"
          gap={3}
          className="align-items-end flex-wrap"
        >
          <Form.Group controlId="geo-select-season">
            <Form.Label>Season</Form.Label>
            <Form.Select
              value={seasonId}
              onChange={(event) => setSeasonId(event.target.value)}
              disabled={isLoadingFilters}
            >
              <option value="">All seasons</option>
              {seasons.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <EventSelector
            events={events}
            selected={eventUUID}
            setSelected={setEventUUID}
            isLoading={isLoadingFilters}
            groupByYear
            emptyOptionText="All events"
          />
          <ReportRefreshButton
            onRefresh={refresh}
            isRefreshing={isLoadingReport}
            disabled={isLoadingReport}
          />
        </Stack>
      </Card>

      {loadError ? (
        <p className="text-danger mt-3 mb-0">{loadError}</p>
      ) : isLoading && !reportData ? (
        <PageLoadingContainer style="without-sidebar" />
      ) : (
        <>
          <div className="mt-3">
            <GeographicSalesIntelligenceSummary
              kpis={reportData?.kpis}
              coverage={reportData?.coverage}
            />
          </div>

          <Card className="card--xs card-with-border mt-3">
            <Card.Body>
              <h2 className="h5 mb-3">Buyer heatmap</h2>
              <GeographicSalesIntelligenceHeatmap
                heatmap={reportData?.heatmap}
                venues={reportData?.venues}
              />
            </Card.Body>
          </Card>

          <Row className="g-3 mt-0">
            <Col lg={7}>
              <Card className="card--xs card-with-border h-100">
                <Card.Body>
                  <h2 className="h5 mb-3">Distance from venue</h2>
                  <GeographicSalesIntelligenceDistance
                    buckets={reportData?.distanceBuckets}
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col lg={5}>
              <Card className="card--xs card-with-border h-100">
                <Card.Body>
                  <h2 className="h5 mb-3">Local / out of market</h2>
                  <p className="text-muted small mb-2">
                    Local is within {reportData?.localMarketMiles || 50} miles
                    of the venue.
                  </p>
                  <GeographicSalesIntelligenceLocalMarket
                    localVsOutOfMarket={reportData?.localVsOutOfMarket}
                    localMarketMiles={reportData?.localMarketMiles}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="card--xs card-with-border mt-3">
            <Card.Body>
              <h2 className="h5 mb-3">Sales by state</h2>
              <GeographicSalesIntelligenceStateMap
                states={reportData?.states}
              />
            </Card.Body>
          </Card>

          <Card className="card--xs card-with-border mt-3">
            <Card.Body>
              <h2 className="h5 mb-3">Top markets</h2>
              <GeographicSalesIntelligenceTopMarkets
                markets={reportData?.topMarkets}
              />
            </Card.Body>
          </Card>
        </>
      )}
    </section>
  )
}
